<template>
  <div class="tech-page">
    <section class="tech-hero">
      <div class="tech-hero-inner">
        <h1>How it works</h1>
        <p class="lead">
          An in-scanner robotic system with closed-loop image guidance and high accuracy.
        </p>
      </div>
    </section>

    <section class="tech-solution">
      <div class="tech-inner">
        <h2>The challenge</h2>
        <p>Hundreds of thousands of patients could benefit from minimally invasive needle-based treatment each year; only a fraction are treated. Static scans and mental planning limit accuracy and make adoption difficult.</p>
        <h2>Our approach</h2>
        <p class="solution-intro">
          Sensor fusion and closed-loop image guidance—with a workflow that’s simple, streamlined, and consistent.
        </p>
      </div>
    </section>

    <section class="tech-workflow">
      <div class="workflow-diagram-wrap">
        <div class="workflow-diagram">
          <h3 class="workflow-diagram-title">Our Workflow:</h3>
          <div class="workflow-flow">
            <div class="workflow-node">
              <div class="workflow-node-inner">
                <div class="workflow-node-visual">
                  <img src="@/assets/image-acquisition.png" alt="Image acquisition" />
                </div>
                <h4>Image acquisition</h4>
                <p>CT imaging for planning and guidance</p>
              </div>
            </div>
            <div class="workflow-connector" aria-hidden="true"><span class="workflow-arrow"></span></div>
            <div class="workflow-node">
              <div class="workflow-node-inner">
                <div class="workflow-node-visual">
                  <img src="@/assets/assisted-surgical-planning.gif" alt="Assisted surgical planning" />
                </div>
                <h4>Assisted surgical planning</h4>
                <p>Planning for insertion angles and margins</p>
              </div>
            </div>
            <div class="workflow-connector" aria-hidden="true"><span class="workflow-arrow"></span></div>
            <div class="workflow-node">
              <div class="workflow-node-inner">
                <div class="workflow-node-visual">
                  <img src="@/assets/robotic-instrument-positioning.png" alt="Robotic instrument positioning" />
                </div>
                <h4>Robotic instrument positioning</h4>
                <p>Patient tracking and alignment of the needle guide to the planned trajectory</p>
              </div>
            </div>
            <div class="workflow-connector" aria-hidden="true"><span class="workflow-arrow"></span></div>
            <div class="workflow-node">
              <div class="workflow-node-inner">
                <div class="workflow-node-visual">
                  <img src="@/assets/cooperative-insertion.gif" alt="Cooperative insertion" />
                </div>
                <h4>Cooperative insertion</h4>
                <p>Guided fine adjustments and closed-loop image-guided insertion</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="tech-capabilities">
      <div class="tech-inner">
        <h2>What we deliver</h2>
        <ul class="cap-list">
          <li><strong>High accuracy</strong> — Evaluated in preclinical studies; designed to meet clinical accuracy targets</li>
          <li><strong>Short setup time</strong> — Designed for under 20 minutes so the system fits into your workflow</li>
          <li><strong>In-scanner operation</strong> — Robot operates in the scanner room for direct image guidance</li>
          <li><strong>Closed-loop control</strong> — Real-time feedback and guidance during insertion, not just planning</li>
        </ul>
      </div>
    </section>

    <section class="tech-cta">
          <router-link to="/contact" class="btn">Get in touch</router-link>
    </section>
  </div>
</template>

<script>
export default {
  name: 'TechnologyPage',
  mounted() {
    document.title = 'Technology | Air Surgical';
  }
};
</script>

<style scoped>
.tech-page {
  padding-top: 70px;
}

.tech-hero {
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
  color: #fff;
  padding: 80px 24px 60px;
  text-align: center;
}

.tech-hero-inner {
  max-width: 720px;
  margin: 0 auto;
}

.tech-hero h1 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.25;
}

.tech-hero .lead {
  font-size: 1.3125rem;
  line-height: 1.6;
  opacity: 0.95;
}

.tech-solution {
  padding: 24px 24px 20px;
  background: #fff;
}

.tech-capabilities,
.tech-outcomes {
  padding: 40px 24px;
  background: #fff;
}

.tech-workflow {
  padding: 28px 24px 72px;
  background: #f8fafc;
}

.tech-inner {
  max-width: 680px;
  margin: 0 auto;
}

.tech-inner h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
}
.tech-inner h2:first-child {
  margin-top: 0;
}

.tech-inner h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.75rem;
}

.tech-inner p {
  font-size: 1.0625rem;
  line-height: 1.65;
  color: #444;
  margin-bottom: 0.75rem;
}

.tech-solution .tech-inner h2 {
  margin-top: 1.25rem;
}
.tech-solution .tech-inner h2:first-child {
  margin-top: 0;
}
.tech-solution .tech-inner p {
  margin-bottom: 0.5rem;
}
.solution-intro {
  margin-bottom: 0;
}

.workflow-diagram-wrap {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
}

.workflow-diagram {
  margin-top: 0;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.workflow-diagram-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e3a5f;
  margin-bottom: 1.25rem;
  text-align: center;
}

.workflow-flow {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: center;
  gap: 0;
}

.workflow-node {
  flex: 1 1 100%;
  min-width: 0;
}

.workflow-node-inner {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  height: 100%;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;
}

.workflow-node-inner:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.workflow-node-visual {
  width: 100%;
  aspect-ratio: 4/3;
  min-height: 180px;
  border-radius: 10px;
  overflow: hidden;
  background: #f8fafc;
  margin-bottom: 0.75rem;
}

.workflow-node-visual img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.workflow-node-inner h4 {
  font-size: 1.0625rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.workflow-node-inner p {
  font-size: 0.875rem;
  line-height: 1.5;
  color: #64748b;
  margin-bottom: 0;
}

.workflow-node-inner p:last-of-type {
  margin-bottom: 0;
}

.workflow-connector {
  flex: 0 0 auto;
  display: none;
  align-items: center;
  padding: 0 0.5rem;
  align-self: center;
}

.workflow-arrow {
  width: 24px;
  height: 24px;
  border-right: 2px solid #cbd5e1;
  border-bottom: 2px solid #cbd5e1;
  transform: rotate(-45deg);
}


@media (min-width: 640px) {
  .workflow-connector {
    padding: 0 0.25rem;
  }

  .workflow-arrow {
    width: 20px;
    height: 20px;
  }
}

@media (min-width: 768px) {
  .workflow-node {
    flex: 1 1 45%;
    max-width: 280px;
  }

  .workflow-node-visual {
    aspect-ratio: 4/3;
    min-height: 220px;
  }
}

@media (min-width: 1100px) {
  .workflow-flow {
    flex-wrap: nowrap;
    justify-content: center;
    gap: 0;
  }

  .workflow-node {
    flex: 0 1 220px;
    max-width: 240px;
  }

  .workflow-node-visual {
    min-height: 200px;
    aspect-ratio: 4/3;
  }

  .workflow-connector {
    display: flex;
    flex: 0 0 28px;
    padding: 0;
  }

  .workflow-arrow {
    width: 24px;
    height: 24px;
    border-right: 2.5px solid #2563eb;
    border-bottom: 2.5px solid #2563eb;
  }

}

.cap-list,
.outcomes-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
}

.cap-list li,
.outcomes-list li {
  padding: 0.4rem 0 0.4rem 1.5rem;
  position: relative;
  font-size: 1rem;
  line-height: 1.5;
  color: #444;
}

.cap-list li::before,
.outcomes-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #2563eb;
  font-weight: 600;
}

.outcomes-note {
  margin-top: 1rem;
  margin-bottom: 0;
}

.tech-cta {
  padding: 48px 24px;
  text-align: center;
  background: #fff;
}

.tech-cta .btn {
  display: inline-block;
  background-color: #2563eb;
  color: #fff;
  font-weight: 600;
  padding: 14px 28px;
  border-radius: 8px;
  text-decoration: none;
  transition: background-color 0.2s;
}

.tech-cta .btn:hover {
  background-color: #1d4ed8;
}

@media (min-width: 768px) {
  .tech-hero h1 {
    font-size: 2.5rem;
  }

  .tech-hero .lead {
    font-size: 1.375rem;
  }
}
</style>
